// src/components/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import { Link } from 'react-router-dom';
import { 
  Users, Server, Key, RefreshCw, AlertTriangle, 
  CheckCircle, Clock, Calendar, UserPlus, Database,
  Settings, BarChart, Shield, ExternalLink
} from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import BusinessCentralService from '../../services/businessCentralService';

const AdminDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    users: {
      total: 0,
      active: 0,
      pending: 0,
      inactive: 0
    },
    environments: {
      total: 0,
      active: 0
    },
    auth: {
      status: 'Unknown',
      expiresAt: null,
      daysRemaining: 0
    },
    tenantInfo: {
      companyName: '',
      id: '',
      registrationDate: ''
    }
  });
  
  const { user, getAccessToken, activeEnvironment } = useAuth();
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        // Initialize Business Central service
        const bcService = new BusinessCentralService(getAccessToken);
        
        // Set active environment if available
        if (activeEnvironment) {
          bcService.setActiveEnvironment(activeEnvironment.id, activeEnvironment.name);
        }
        
        // Fetch portal users
        let users = [];
        try {
          users = await bcService.getPortalUsers();
        } catch (error) {
          logger.error('Error fetching portal users:', error);
          // Mock data for development
          if (process.env.NODE_ENV === 'development') {
            users = [
              { id: '1', status: 'Active' },
              { id: '2', status: 'Active' },
              { id: '3', status: 'PendingFirstLogin' },
              { id: '4', status: 'Inactive' },
              { id: '5', status: 'Active' }
            ];
          }
        }
        
        // Fetch environments
        let environments = [];
        try {
          environments = await bcService.getAvailableEnvironments();
        } catch (error) {
          logger.error('Error fetching environments:', error);
          // Mock data for development
          if (process.env.NODE_ENV === 'development') {
            environments = [
              { id: 'env1', status: 'Active' },
              { id: 'env2', status: 'Active' }
            ];
          }
        }
        
        // Fetch authorization status
        let authStatus = {};
        try {
          authStatus = await bcService.getAuthorizationStatus();
        } catch (error) {
          logger.error('Error fetching authorization status:', error);
          // Mock data for development
          if (process.env.NODE_ENV === 'development') {
            authStatus = {
              status: 'Active',
              expiresAt: new Date(Date.now() + 86400000 * 67).toISOString() // 67 days from now
            };
          }
        }
        
        // Fetch tenant information
        let tenantInfo = {};
        try {
          tenantInfo = await bcService.getTenantInfo();
        } catch (error) {
          logger.error('Error fetching tenant info:', error);
          // Mock data for development
          if (process.env.NODE_ENV === 'development') {
            tenantInfo = {
              companyName: 'Demo Company Ltd',
              id: 'tenant-123',
              registrationDate: '2025-01-15'
            };
          }
        }
        
        // Count user statuses
        const activeUsers = users.filter(u => u.status === 'Active').length;
        const pendingUsers = users.filter(u => u.status === 'PendingFirstLogin').length;
        const inactiveUsers = users.filter(u => u.status === 'Inactive').length;
        
        // Calculate days remaining for token expiration
        const expiresAt = authStatus.expiresAt ? new Date(authStatus.expiresAt) : null;
        const now = new Date();
        const daysRemaining = expiresAt ? Math.floor((expiresAt - now) / (1000 * 60 * 60 * 24)) : 0;
        
        // Update dashboard data
        setDashboardData({
          users: {
            total: users.length,
            active: activeUsers,
            pending: pendingUsers,
            inactive: inactiveUsers
          },
          environments: {
            total: environments.length,
            active: environments.filter(e => e.status === 'Active').length
          },
          auth: {
            status: authStatus.status || 'Unknown',
            expiresAt: authStatus.expiresAt,
            daysRemaining
          },
          tenantInfo: {
            name: tenantInfo.companyName || activeEnvironment?.name || 'Unknown',
            id: tenantInfo.id || activeEnvironment?.id || 'Unknown',
            registrationDate: tenantInfo.registrationDate || '2025-01-01'
          }
        });
      } catch (error) {
        logger.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [activeEnvironment]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  // Dashboard widgets
  const QuickActionCard = ({ icon: Icon, title, description, to, buttonText, color }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className={`flex-shrink-0 rounded-md p-3 ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd>
                <div className="text-lg font-medium text-gray-900">{description}</div>
              </dd>
            </dl>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 px-5 py-3">
        <div className="text-sm">
          <Link
            to={to}
            className="font-medium text-indigo-700 hover:text-indigo-900"
          >
            {buttonText} <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </div>
    </div>
  );
  
  const StatCard = ({ title, value, icon: Icon, color, secondaryValue, secondaryLabel }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className={`flex-shrink-0 rounded-md p-3 ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd>
                <div className="text-lg font-medium text-gray-900">{value}</div>
                {secondaryValue && (
                  <div className="text-sm text-gray-500">{secondaryLabel}: {secondaryValue}</div>
                )}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  // Get auth status badge color
  const getAuthStatusColor = () => {
    const { daysRemaining, status } = dashboardData.auth;
    
    if (status === 'Inactive' || status === 'Expired' || daysRemaining <= 0) {
      return 'bg-red-100 text-red-800';
    }
    
    if (daysRemaining <= 14) {
      return 'bg-yellow-100 text-yellow-800';
    }
    
    return 'bg-green-100 text-green-800';
  };
  
  return (
    <div className="space-y-6">
      {/* Header with tenant info */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Admin Dashboard
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Management console for Business Central Employee Portal
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <div className="flex flex-col items-end">
              <div className="text-sm font-medium text-gray-500">Tenant</div>
              <div className="text-lg font-semibold text-gray-900">{dashboardData.tenantInfo.companyName}</div>
              <div className="text-xs text-gray-500">Registered: {formatDate(dashboardData.tenantInfo.registrationDate)}</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats overview */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Users"
          value={dashboardData.users.total}
          icon={Users}
          color="bg-indigo-500"
          secondaryValue={dashboardData.users.active}
          secondaryLabel="Active"
        />
        <StatCard
          title="Environments"
          value={dashboardData.environments.total}
          icon={Server}
          color="bg-blue-500"
        />
        <StatCard
          title="Authentication Status"
          value={
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getAuthStatusColor()}`}>
              {dashboardData.auth.status}
            </span>
          }
          icon={Key}
          color="bg-purple-500"
          secondaryValue={`${dashboardData.auth.daysRemaining} days`}
          secondaryLabel="Remaining"
        />
        <StatCard
          title="Active Environment"
          value={activeEnvironment?.name || 'None'}
          icon={Database}
          color="bg-green-500"
          secondaryValue={activeEnvironment?.type || 'N/A'}
          secondaryLabel="Type"
        />
      </div>
      
      {/* Auth status alert */}
      {dashboardData.auth.daysRemaining <= 14 && (
        <div className={`rounded-md p-4 ${
          dashboardData.auth.daysRemaining <= 7 ? 'bg-red-50' : 'bg-yellow-50'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className={`h-5 w-5 ${
                dashboardData.auth.daysRemaining <= 7 ? 'text-red-400' : 'text-yellow-400'
              }`} />
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${
                dashboardData.auth.daysRemaining <= 7 ? 'text-red-800' : 'text-yellow-800'
              }`}>
                Authentication Token Expiring Soon
              </h3>
              <div className={`mt-2 text-sm ${
                dashboardData.auth.daysRemaining <= 7 ? 'text-red-700' : 'text-yellow-700'
              }`}>
                <p>
                  Your authentication token will expire in {dashboardData.auth.daysRemaining} days. 
                  Please refresh your authorization to ensure uninterrupted access to Business Central.
                </p>
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <Link
                    to="/admin/authorization"
                    className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                      dashboardData.auth.daysRemaining <= 7 
                        ? 'bg-red-100 text-red-800 hover:bg-red-200'
                        : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                    }`}
                  >
                    Manage Authorization
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Quick actions */}
      <h2 className="text-lg font-medium text-gray-900 mt-8">Quick Actions</h2>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <QuickActionCard
          icon={UserPlus}
          title="Add User"
          description="Create a new portal user account"
          to="/admin/users"
          buttonText="Add User"
          color="bg-green-500"
        />
        <QuickActionCard
          icon={Server}
          title="Add Environment"
          description="Connect a new Business Central environment"
          to="/admin/environments"
          buttonText="Add Environment"
          color="bg-blue-500"
        />
        <QuickActionCard
          icon={RefreshCw}
          title="Refresh Authorization"
          description="Update Business Central OAuth authorization"
          to="/admin/authorization"
          buttonText="Go to Authorization"
          color="bg-purple-500"
        />
      </div>
      
      {/* User status overview */}
      <h2 className="text-lg font-medium text-gray-900 mt-8">User Status Overview</h2>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Portal Users
            </h3>
            <Link
              to="/admin/users"
              className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
            >
              View All Users
            </Link>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="bg-green-50 shadow-sm rounded-lg p-4 border border-green-100">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Active</h4>
                  <p className="text-2xl font-semibold text-green-600">{dashboardData.users.active}</p>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 shadow-sm rounded-lg p-4 border border-yellow-100">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-yellow-500" />
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Pending</h4>
                  <p className="text-2xl font-semibold text-yellow-600">{dashboardData.users.pending}</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 shadow-sm rounded-lg p-4 border border-gray-200">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 text-gray-400" />
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Inactive</h4>
                  <p className="text-2xl font-semibold text-gray-600">{dashboardData.users.inactive}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Business Central resources card */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Business Central Resources
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <a
              href="https://learn.microsoft.com/en-us/dynamics365/business-central/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <div className="flex-shrink-0">
                <ExternalLink className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <h4 className="text-sm font-medium text-blue-900">
                  Business Central Documentation
                </h4>
                <p className="text-xs text-blue-700 mt-1">
                  Official Microsoft documentation for Business Central
                </p>
              </div>
            </a>
            <a
              href="https://learn.microsoft.com/en-us/dynamics365/business-central/dev-itpro/administration/automation-apis-using-s2s-authentication"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <div className="flex-shrink-0">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <h4 className="text-sm font-medium text-purple-900">
                  Service-to-Service Authentication Guide
                </h4>
                <p className="text-xs text-purple-700 mt-1">
                  Learn about OAuth and service-to-service authentication
                </p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;